import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";

export interface CreateSelfTransferPayload {
  TransferGround: string;
  NameOfHODUnit: string;
  Designation?: string;
  Department?: string;
  TransferUnder?: string;
  TransferUnderDesignation?: string;
  ReasonForTransfer: string;
  Remarks: string;
  TransferPeriod?: number;
  YearMonthDays?: string;
}

export interface ApproveRejectSelfTransferPayload {
  RequestID: string;
  Action: "A" | "R";
  EmpID?: string;
  ApproverID?: string;
  Check?: string;
  SpecialComment?: string;
  ApproverComment?: string;
  NodalRemarksRegardingAvailability?: string;
}

export interface ApproverItem {
  id: string;
  requestId: string;
  level: string;
  employeeCode: string;
  name: string;
  status: string;
  date: string;
  remark: string;
  specialComment?: string;
  nodalRemarks?: string;
}

export const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "INPROCESS", label: "In Process" },
  { value: "APPROVE", label: "Approved" },
  { value: "REJECT", label: "Rejected" },
];

export function unwrap(res: any): any {
  if (!res) return null;
  return res?.data ?? res;
}

export function extractList(res: any): any[] {
  const root = unwrap(res);
  const inner = root?.data ?? root;
  if (Array.isArray(inner?.items)) return inner.items;
  if (Array.isArray(inner?.data?.items)) return inner.data.items;
  if (Array.isArray(inner?.d?.results)) return inner.d.results;
  if (Array.isArray(inner?.results)) return inner.results;
  if (Array.isArray(inner?.data)) return inner.data;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(root?.items)) return root.items;
  if (Array.isArray(root?.d?.results)) return root.d.results;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root)) return root;
  return [];
}

export function extractOne(res: any): any {
  const root = unwrap(res);
  const inner = root?.data ?? root;
  if (inner?.data && typeof inner.data === "object" && !Array.isArray(inner.data)) {
    return inner.data;
  }
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner;
  }
  if (root && typeof root === "object" && !Array.isArray(root)) {
    return root;
  }
  return null;
}

export function apiMessage(res: any, defaultMsg = "Operation completed"): string {
  const root = unwrap(res);
  const msg =
    root?.message ||
    root?.data?.message ||
    root?.response?.message ||
    res?.message ||
    defaultMsg;
  return String(msg);
}

export function getLoggedInUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    return parsed?.data?.user || parsed?.user || parsed || {};
  } catch {
    return {};
  }
}

export function getLoggedInEmpId(): string {
  const user = getLoggedInUser();
  return String(
    user?.empId || user?.details?.empId || user?.usrid || user?.emp_id || user?.employee?.empId || ""
  ).trim();
}

export function getApproverEmpId(): string {
  return getLoggedInEmpId();
}

/** Handles formatting ISO dates, DotNet /Date(ms)/ format, and timestamp strings */
export function formatDateDisplay(raw?: string | null): string {
  if (!raw) return "-";
  try {
    if (typeof raw === "string" && raw.startsWith("/Date(")) {
      const ms = parseInt(raw.replace(/[^0-9]/g, ""), 10);
      if (!isNaN(ms)) {
        const d = new Date(ms);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
      }
    }
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  } catch {
    return String(raw);
  }
}

export function groundLabel(g?: string): string {
  if (!g) return "-";
  const s = String(g).trim().toUpperCase();
  if (s === "INTRA") return "Intra Department";
  if (s === "INTER") return "Inter Department";
  return g;
}

export function reasonLabel(r?: string): string {
  if (!r) return "-";
  const s = String(r).trim().toUpperCase();
  if (s === "PER" || s === "PERMANENT") return "Permanent";
  if (s === "TEM" || s === "TEMPORARY") return "Temporary";
  return r;
}

export function formatHodUnit(hod?: string | null): string {
  if (!hod) return "Intra-Dept HOD";
  const s = String(hod).trim();
  if (s === "00000000" || s === "0" || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") {
    return "Intra-Dept HOD";
  }
  return s;
}

export function resolveApproverStatus(rawStatus?: string): string {
  if (!rawStatus) return "";
  const s = String(rawStatus).trim().toUpperCase();
  if (s === "APPROVE" || s === "APPROVED" || s === "A") return "Approved";
  if (s === "REJECT" || s === "REJECTED" || s === "R") return "Rejected";
  if (s === "PENDING" || s === "PENDING APPROVAL") return "Pending";
  if (s === "INPROCESS" || s === "IN_PROCESS" || s === "T") return "In Process";
  return s;
}

export function resolveStatusBadge(status?: string): {
  label: string;
  badgeClass: string;
  bg: string;
  text: string;
  border: string;
  dotColor: string;
} {
  const s = String(status || "").trim().toUpperCase();
  if (s === "APPROVE" || s === "APPROVED" || s === "A") {
    return {
      label: "APPROVED",
      badgeClass: "badge bg-success-subtle text-success border border-success-subtle",
      bg: "rgba(34, 197, 94, 0.1)",
      text: "#15803D",
      border: "#86EFAC",
      dotColor: "#22C55E",
    };
  }
  if (s === "REJECT" || s === "REJECTED" || s === "R") {
    return {
      label: "REJECTED",
      badgeClass: "badge bg-danger-subtle text-danger border border-danger-subtle",
      bg: "rgba(239, 68, 68, 0.1)",
      text: "#B91C1C",
      border: "#FCA5A5",
      dotColor: "#EF4444",
    };
  }
  if (s === "PENDING" || s === "PENDING APPROVAL") {
    return {
      label: "PENDING",
      badgeClass: "badge bg-light text-muted border border-secondary-subtle",
      bg: "rgba(148, 163, 184, 0.1)",
      text: "#64748B",
      border: "#CBD5E1",
      dotColor: "#94A3B8",
    };
  }
  return {
    label: "IN PROCESS",
    badgeClass: "badge bg-warning-subtle text-warning-emphasis border border-warning-subtle",
    bg: "rgba(245, 158, 11, 0.1)",
    text: "#B45309",
    border: "#FDE68A",
    dotColor: "#F59E0B",
  };
}

export function calculateTransferDates(
  periodValue: number,
  periodUnit: string
): { from: string; to: string } | null {
  if (isNaN(periodValue) || periodValue <= 0) return null;
  const fromDate = new Date();
  const toDate = new Date(fromDate);

  const unit = String(periodUnit || "").trim().toUpperCase();
  if (unit === "YEAR" || unit.startsWith("Y")) {
    toDate.setFullYear(toDate.getFullYear() + periodValue);
  } else if (unit === "MONTH" || unit.startsWith("M")) {
    toDate.setMonth(toDate.getMonth() + periodValue);
  } else if (unit === "DAY" || unit.startsWith("D")) {
    toDate.setDate(toDate.getDate() + periodValue);
  }

  const format = (d: Date) => {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  return {
    from: format(fromDate),
    to: format(toDate),
  };
}

export function parseApprovers(details: any): ApproverItem[] {
  if (!details) return [];
  const requestId = details.RequestID || details.referenceNumber || details.id || "-";
  let list: ApproverItem[] = [];

  const sapApprovers = Array.isArray(details.ToApprovers?.results)
    ? details.ToApprovers.results
    : Array.isArray(details.ToApprovers)
    ? details.ToApprovers
    : [];

  if (sapApprovers.length > 0) {
    list = sapApprovers.map((a: any, idx: number) => {
      const localR = Array.isArray(details.remarks)
        ? details.remarks.find(
            (r: any) => r.Level === (a.Level ?? idx + 1) || r.ApproverEmpID === a.EmpID
          )
        : null;
      const approverStatus = resolveApproverStatus(a.Status || localR?.Status);
      const isActed = approverStatus === "Approved" || approverStatus === "Rejected";
      const rawDate = a.Date || (isActed ? (localR?.Date || localR?.updated_at) : null);
      const rawRemark = a.Remark || localR?.Remark || localR?.ApproverComment || "";
      return {
        id: String(idx + 1),
        requestId: String(requestId),
        level: String(a.Level || idx + 1),
        employeeCode: a.EmpID || localR?.ApproverEmpID || "-",
        name: a.Name || localR?.ApproverName || `Approver ${idx + 1}`,
        status: approverStatus,
        date: rawDate ? formatDateDisplay(rawDate) : "",
        remark: rawRemark !== "-" && rawRemark !== "—" ? rawRemark : "",
        specialComment: a.SpecialComment || localR?.SpecialComment || "",
        nodalRemarks:
          a.NodalRemarksRegardingAvailability ||
          localR?.NodalRemarksRegardingAvailability ||
          "",
      };
    });
  } else if (Array.isArray(details.remarks) && details.remarks.length > 0) {
    list = details.remarks.map((r: any, idx: number) => {
      const approverStatus = resolveApproverStatus(r.Status);
      const isActed = approverStatus === "Approved" || approverStatus === "Rejected";
      const rawDate = r.Date || (isActed ? (r.updated_at || r.created_at) : null);
      const rawRemark = r.ApproverComment || r.Remark || "";
      return {
        id: String(r.id || idx + 1),
        requestId: String(r.RequestID || requestId),
        level: String(r.Level || idx + 1),
        employeeCode: r.ApproverEmpID || "-",
        name: r.ApproverName || `Approver ${idx + 1}`,
        status: approverStatus,
        date: rawDate ? formatDateDisplay(rawDate) : "",
        remark: rawRemark !== "-" && rawRemark !== "—" ? rawRemark : "",
        specialComment: r.SpecialComment || "",
        nodalRemarks: r.NodalRemarksRegardingAvailability || "",
      };
    });
  }

  // Fallback: If no approvers list returned yet, synthesize level 1 from main request if ApproverID exists
  if (list.length === 0 && details.ApproverID) {
    const approverStatus = resolveApproverStatus(details.Status);
    const isActed = approverStatus === "Approved" || approverStatus === "Rejected";
    const rawRemark = details.ApproverComment || details.Remarks || "";
    list = [
      {
        id: "1",
        requestId: String(requestId),
        level: "1",
        employeeCode: details.ApproverID,
        name: details.ApproverName || `Approver (${details.ApproverID})`,
        status: approverStatus,
        date: isActed ? formatDateDisplay(details.updated_at || details.created_at) : "",
        remark: rawRemark !== "-" && rawRemark !== "—" ? rawRemark : "",
        specialComment: details.SpecialComment || "",
        nodalRemarks: details.NodalRemarksRegardingAvailability || "",
      },
    ];
  }

  // Sequential workflow activation:
  // An approver level is only "In Process" if all preceding levels have been Approved.
  // Subsequent unreached levels are "Pending" and must not show "In Process".
  const overall = String(details.Status || details.status || "").trim().toUpperCase();
  const isOverallRejected = ["REJECT", "REJECTED", "R"].includes(overall);
  const isOverallCancelled = ["CANCEL", "CANCELLED", "WITHDRAWN"].includes(overall);

  let hasFoundCurrentActive = false;

  return list.map((item, idx) => {
    const rawS = String(item.status || "").trim().toUpperCase();
    if (["APPROVE", "APPROVED", "A"].includes(rawS)) {
      return { ...item, status: "Approved" };
    }
    if (["REJECT", "REJECTED", "R"].includes(rawS)) {
      return { ...item, status: "Rejected" };
    }

    if (isOverallRejected || isOverallCancelled) {
      return { ...item, status: "Pending" };
    }

    const allPriorApproved =
      idx === 0 ||
      list.slice(0, idx).every((prev) => {
        const ps = String(prev.status || "").trim().toUpperCase();
        return ["APPROVE", "APPROVED", "A"].includes(ps);
      });

    if (allPriorApproved && !hasFoundCurrentActive) {
      hasFoundCurrentActive = true;
      return { ...item, status: "In Process" };
    }

    return { ...item, status: "Pending" };
  });
}

// ─── API Services ─────────────────────────────────────────────────────────────

export const fetchTransferGrounds = async () => {
  try {
    const res = await Api.post(API_URLS.SELF_TRANSFER.TRANSFER_GROUNDS, {});
    const list = extractList(res);
    if (list && list.length > 0) {
      const seen = new Set<string>();
      const unique = list.filter((item: any) => {
        const key = String(item.TransferGround || item.value || "").trim().toUpperCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (unique.length > 0) return { success: true, data: unique };
    }
  } catch (e) {
    console.warn("fetchTransferGrounds fallback used:", e);
  }
  return {
    success: true,
    data: [
      { TransferGround: "INTRA", TransferGroundText: "Intra Department" },
      { TransferGround: "INTER", TransferGroundText: "Inter Department" },
    ],
  };
};

export const fetchReasonForTransfer = async () => {
  try {
    const res = await Api.post(API_URLS.SELF_TRANSFER.REASON_FOR_TRANSFER, {});
    const list = extractList(res);
    if (list && list.length > 0) {
      const seen = new Set<string>();
      const normalized = list
        .map((r: any) => {
          const rawVal = String(
            r.ReasonForTransfer || r.Value || r.DomvalueL || r.value || r.code || ""
          ).trim().toUpperCase();
          const val =
            rawVal === "TEMPORARY" || rawVal === "TEMP"
              ? "TEM"
              : rawVal === "PERMANENT" || rawVal === "PERM"
              ? "PER"
              : rawVal;
          const text =
            r.ReasonForTransferText ||
            r.Ddtext ||
            r.label ||
            r.text ||
            (val === "PER" ? "Permanent" : val === "TEM" ? "Temporary" : val);
          return {
            ReasonForTransfer: val,
            ReasonForTransferText: text,
            Value: val,
            Ddtext: text,
          };
        })
        .filter((r: any) => {
          if (!r.ReasonForTransfer || seen.has(r.ReasonForTransfer)) return false;
          seen.add(r.ReasonForTransfer);
          return true;
        });
      if (normalized.length > 0) return { success: true, data: normalized };
    }
  } catch (e) {
    console.warn("fetchReasonForTransfer fallback used:", e);
  }
  return {
    success: true,
    data: [
      { ReasonForTransfer: "PER", ReasonForTransferText: "Permanent", Value: "PER", Ddtext: "Permanent" },
      { ReasonForTransfer: "TEM", ReasonForTransferText: "Temporary", Value: "TEM", Ddtext: "Temporary" },
    ],
  };
};

export const fetchTransferPeriodOptions = async () => {
  try {
    const res = await Api.post(API_URLS.SELF_TRANSFER.TRANSFER_PERIOD, {});
    const list = extractList(res);
    if (list && list.length > 0) {
      const seen = new Set<string>();
      const normalized = list
        .map((p: any) => {
          const rawVal = String(
            p.TransferPeriod || p.Value || p.DomvalueL || p.value || ""
          ).trim().toUpperCase();
          let val = rawVal;
          if (rawVal.startsWith("Y")) val = "YEAR";
          else if (rawVal.startsWith("M")) val = "MONTH";
          else if (rawVal.startsWith("D")) val = "DAY";
          const text =
            p.TransferPeriodText ||
            p.Ddtext ||
            p.label ||
            p.text ||
            (val === "YEAR" ? "Year(s)" : val === "MONTH" ? "Month(s)" : val === "DAY" ? "Day(s)" : val);
          return {
            TransferPeriod: val,
            TransferPeriodText: text,
            Value: val,
            Ddtext: text,
          };
        })
        .filter((p: any) => {
          if (!p.TransferPeriod || seen.has(p.TransferPeriod)) return false;
          seen.add(p.TransferPeriod);
          return true;
        });
      if (normalized.length > 0) return { success: true, data: normalized };
    }
  } catch (e) {
    console.warn("fetchTransferPeriodOptions fallback used:", e);
  }
  return {
    success: true,
    data: [
      { TransferPeriod: "YEAR", TransferPeriodText: "Year(s)", Value: "YEAR", Ddtext: "Year(s)" },
      { TransferPeriod: "MONTH", TransferPeriodText: "Month(s)", Value: "MONTH", Ddtext: "Month(s)" },
      { TransferPeriod: "DAY", TransferPeriodText: "Day(s)", Value: "DAY", Ddtext: "Day(s)" },
    ],
  };
};

export const fetchHodList = async (transferGroup: string) => {
  try {
    const res = await Api.post(API_URLS.SELF_TRANSFER.HOD_LIST, { transferGroup });
    const list = extractList(res);
    return { success: true, data: list };
  } catch (err: any) {
    return { success: false, data: [], message: apiMessage(err, "Failed to load HOD list") };
  }
};

export const fetchTransferUnderList = async (transferGroup: string) => {
  try {
    const res = await Api.post(API_URLS.SELF_TRANSFER.TRANSFER_UNDER_LIST, { transferGroup });
    const list = extractList(res);
    return { success: true, data: list };
  } catch (err: any) {
    return { success: false, data: [], message: apiMessage(err, "Failed to load Transfer Under list") };
  }
};

export const fetchSelfTransferFormPrefill = async (requestId?: string) => {
  try {
    const res = await Api.post(API_URLS.SELF_TRANSFER.FORM_PREFILL, { requestId });
    const data = extractOne(res);
    return { success: true, data };
  } catch (err: any) {
    return { success: false, data: null, message: apiMessage(err, "Failed to load profile prefill") };
  }
};

export const createSelfTransferRequest = async (payload: CreateSelfTransferPayload) => {
  const res = await Api.post(API_URLS.SELF_TRANSFER.CREATE, payload);
  const data = unwrap(res);
  const success = res?.success !== false && data?.status !== "error" && data?.status !== 400 && data?.status !== 500;
  return {
    success,
    data,
    message: apiMessage(res, "Request submitted successfully"),
  };
};

export const fetchSelfTransferRequests = async () => {
  try {
    const res = await Api.post(API_URLS.SELF_TRANSFER.ALL, {});
    const list = extractList(res);
    return { success: true, data: list };
  } catch (err: any) {
    return { success: false, data: [], message: apiMessage(err, "Failed to load requests") };
  }
};

export const fetchSelfTransferDetails = async (requestId: string) => {
  try {
    const res = await Api.post(API_URLS.SELF_TRANSFER.DETAILS, { requestId });
    const data = extractOne(res);
    return { success: true, data };
  } catch (err: any) {
    return { success: false, data: null, message: apiMessage(err, "Failed to load details") };
  }
};

export const approveRejectSelfTransfer = async (payload: ApproveRejectSelfTransferPayload) => {
  const res = await Api.post(API_URLS.SELF_TRANSFER.APPROVE_REJECT, payload);
  const data = unwrap(res);
  const success = res?.success !== false && data?.status !== "error" && data?.status !== 400 && data?.status !== 500;
  return {
    success,
    data,
    message: apiMessage(res, payload.Action === "A" ? "Application has been approved" : "Application has been rejected"),
  };
};

export const syncSelfTransferRequests = async (empId?: string) => {
  try {
    const payload = empId ? { empId } : {};
    const res = await Api.post(API_URLS.SELF_TRANSFER.SYNC, payload);
    return { success: true, data: unwrap(res) };
  } catch (err: any) {
    return { success: false, message: apiMessage(err, "Sync failed") };
  }
};
